import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Stack,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Group as GroupIcon,
  WorkOutline as WorkOutlineIcon,
  SwapHoriz as SwapHorizIcon,
  EventBusy as EventBusyIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Schedule } from '../../pages/Dashboard/Dashboard';
import moment from 'moment';

interface Shift {
  shiftName: string;
  shiftId: string;
  notes: string;
  assignedStaff: string[];
  staffNeeded: string;
  startTime: string; // "HH:mm" format
  endTime: string; // "HH:mm" format
  position: string; // position ID
  isRecurring: boolean;
  daysOfWeek?: string[];
  scheduleId: string;
}

interface ShiftCardProps {
  shift: Shift;
  type: 'my' | 'open' | 'other';
  selectedSchedule: Schedule | null;
  currentEmployeeId: string;
  getEmployee: (employeeId: string) => any;
  getPosition: (positionId: string) => any;
  onClick?: () => void;
  onPickUpShift?: (shift: Shift) => void;
  onSwapShift?: (shift: Shift) => void;
  onRequestTimeOff?: (shift: Shift) => void;
  onDelete?: (shift: Shift) => void;
  onRevertCancel?: (shift: Shift) => void;
  showDeleteButton?: boolean;
  isCanceled?: boolean;
  assignments: Record<string, any>;
  disabled?: boolean;
}

const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  type,
  selectedSchedule,
  currentEmployeeId,
  getEmployee,
  getPosition,
  onClick,
  onPickUpShift,
  onSwapShift,
  onRequestTimeOff,
  onDelete,
  onRevertCancel,
  showDeleteButton = false,
  isCanceled = false,
  assignments,
  disabled = false
}) => {
  const assignedNames = shift.assignedStaff
    .map(id => getEmployee(id)?.name || 'Unknown')
    .filter(name => name !== 'Unknown');

  const spotsRemaining = parseInt(shift.staffNeeded) - shift.assignedStaff.length;

  const getShiftDuration = (shift: Shift) => {
    const [startHour, startMin] = shift.startTime.split(':').map(Number);
    const [endHour, endMin] = shift.endTime.split(':').map(Number);

    const start = moment().hours(startHour).minutes(startMin);
    const end = moment().hours(endHour).minutes(endMin);

    // Handle overnight shifts
    if (end.isBefore(start)) {
      end.add(1, 'day');
    }

    const duration = moment.duration(end.diff(start));
    return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
  };

  const formatShiftTime = (timeStr: string) => {
    const [hour, min] = timeStr.split(':').map(Number);
    return moment().hours(hour).minutes(min).format('h:mm A');
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card onClick from firing
    if (onDelete) {
      onDelete(shift);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: 1,
        borderColor: isCanceled ? 'error.main' : 'primary.main',
        backgroundColor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: onClick ? 6 : 'none'
        },
        position: 'relative',
      }}
      onClick={onClick}
    >
      <CardContent>
        {/* Delete Button - Top Right */}
        {showDeleteButton && onDelete && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title="Delete Shift">
              <IconButton
                size="small"
                color="error"
                onClick={handleDelete}
                disabled={disabled}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Avatar
              sx={{
                bgcolor: isCanceled ? 'error.main' : 'primary.main',
                width: 48,
                height: 48
              }}
            >
              <WorkOutlineIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                {shift.shiftName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={getPosition(shift.position)?.positionName || getPosition(shift.position)?.name || 'Unknown Position'}
                  size="small"
                  variant="outlined"
                />
                {shift.isRecurring && (
                  <Chip label="Recurring" size="small" color="info" variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
            {type === 'my' && <Chip label="Your Shift" size="small" color="primary" />}
            {type === 'open' && (
              <Chip
                label={`${spotsRemaining} spot${spotsRemaining !== 1 ? 's' : ''} open`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1.5}>
          {/* Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>
              {formatShiftTime(shift.startTime)} - {formatShiftTime(shift.endTime)}
            </Typography>
            <Chip label={getShiftDuration(shift)} size="small" variant="outlined" />
          </Box>

          {/* Staffing */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>{assignments.length}</strong> of <strong>{shift.staffNeeded}</strong> staff assigned
            </Typography>
          </Box>

          {/* Assigned Staff */}
          {assignments.length > 0  && selectedSchedule?.viewOtherStaff && (
            <Box sx={{ ml: 4 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {assignments.map((id: string) => (
                  <Chip
                    key={id}
                    label={getEmployee(id)?.name}
                    size="small"
                    avatar={
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {getEmployee(id)?.name.charAt(0)}
                      </Avatar>
                    }
                    variant='filled'
                    color='primary'
                  />
                ))}
              </Stack>
            </Box>
          )}
          {assignments.length === 0 && selectedSchedule?.viewOtherStaff && assignedNames.length > 0 && (
            <Box sx={{ ml: 4 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {assignedNames.map((name, idx) => (
                  <Chip
                    key={idx}
                    label={name}
                    size="small"
                    avatar={
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {name.charAt(0)}
                      </Avatar>
                    }
                    variant={name === getEmployee(currentEmployeeId)?.name ? 'filled' : 'outlined'}
                    color={name === getEmployee(currentEmployeeId)?.name ? 'primary' : 'default'}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Notes */}
          {shift.notes && (
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mt: 1 }}>
              <InfoIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {shift.notes}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Action Buttons */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isCanceled && onRevertCancel && (
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onRevertCancel(shift);
              }}
            >
              Revert Cancellation
            </Button>
          )}
          {type === 'open' && onPickUpShift && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onPickUpShift(shift);
              }}
            >
              Pick Up Shift
            </Button>
          )}
          {type === 'my' && (
            <>
              {onSwapShift && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SwapHorizIcon />}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwapShift(shift);
                  }}
                >
                  Swap Shifts
                </Button>
              )}
              {onRequestTimeOff && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<EventBusyIcon />}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestTimeOff(shift);
                  }}
                >
                  Request Off
                </Button>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ShiftCard;